import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Components from "../../../components";
import { FormContainer } from "../Shared";
import {
  useCreateDataAccessAssignmentMutation,
  useUpdateDataAccessAssignmentMutation,
  useGetDataAccessAssignmentQuery,
  useGetVehicleGroupsQuery,
  useGetVehiclesQuery,
  useGetDeviceGroupsQuery,
  useGetDevicesQuery,
  useGetEventTypesQuery,
  useGetRolesQuery,
} from "../services";
import { useForm, useDropDownSearch } from "../../../hooks";
import { dataAccessAssignmentFormValidation } from "../validations";
import {
  buildAssignmentPayload,
  areScopeSelectorsEnabled,
  hasAnyScopeSelection,
} from "./scopeUtils";
import { RoleImpactSummaryModal } from "./RoleImpactSummaryModal";
import { mergeObjects } from "../../../shared/helper";
import { utcToLocalGlobal } from "../../../shared/utility";
import { getLocalizedString } from "../../../shared/translation";
import { CurrentUser } from "../../../shared/currentUser";

// Permission key for gating create/edit/delete. Must match the resourceName
// used by the list container's MPSharedHeader / MPSharedTable so the whole
// feature is gated against a single app_features entry (Requirements 1.4, 1.5).
const RESOURCE_NAME = "Data Access Assignments";

// Default initial values for a new assignment (Requirement 3.1).
const defaultInitialValues = {
  name: "",
  description: "",
  scope_mode: "selected",
  status: "active",
  vehicle_group_ids: [],
  vehicle_ids: [],
  device_group_ids: [],
  device_ids: [],
  event_ids: [],
  role_ids: [],
};

/**
 * Client-side Admin-role protection guard (Requirement 8, design Open Question 5).
 *
 * The Admin role's all-client assignment is identified via a system-role
 * marker on the record. We check the documented `is_system_role` flag plus a
 * couple of defensive fallbacks so the guard holds regardless of the exact
 * marker the backend settles on.
 */
const deriveAdminProtected = (record) =>
  Boolean(record && (record.is_system_role || record.is_admin_role || record.admin_role));

/**
 * Order-independent set comparison for two id arrays. Returns true when both
 * contain exactly the same ids regardless of ordering or duplicates. Used to
 * detect whether the linked-role set changed in edit mode (Requirements 4.7,
 * 9.1, 9.3).
 */
const areRoleSetsEqual = (a, b) => {
  const setA = new Set(Array.isArray(a) ? a : []);
  const setB = new Set(Array.isArray(b) ? b : []);
  if (setA.size !== setB.size) return false;
  for (const id of setA) {
    if (!setB.has(id)) return false;
  }
  return true;
};

/**
 * Formats an audit "by" value that may arrive as a user object
 * ({ first_name, last_name, email }) or a bare string. Returns `null` when the
 * value is absent so the caller can render the "unavailable" placeholder
 * (Requirement 6.4).
 */
const formatAuditUser = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const name = [value.first_name, value.last_name].filter(Boolean).join(" ").trim();
    return name || value.email || null;
  }
  return null;
};

/**
 * Formats an audit date value, returning `null` when absent so the caller can
 * render the "unavailable" placeholder (Requirement 6.4).
 */
const formatAuditDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return utcToLocalGlobal(value);
};

// Read-only, non-focusable audit line (Requirement 6.3) with an "unavailable"
// placeholder for absent values (Requirement 6.4). Rendered as static text,
// never as a form input, so it cannot be focused, changed, or submitted.
const AuditField = ({ label, value }) => (
  <div className="qi-form-element data-access-audit_item">
    <span className="data-access-audit_label">{label}</span>
    <span className="data-access-audit_value" tabIndex={-1} aria-readonly="true">
      {value ?? getLocalizedString("unavailable", "Unavailable")}
    </span>
  </div>
);

export const DataAccessForm = ({ idToEdit = null, onClose }) => {
  // Permission gating (Requirements 1.4, 1.5): when the user lacks manage
  // permission, hide the submit control and render every field read-only so the
  // form is view-only with no create/edit controls. In edit mode we gate on the
  // update permission; in create mode on the create permission.
  //
  const currentUser = new CurrentUser(RESOURCE_NAME);
  const canManage = idToEdit ? currentUser.canUpdate : currentUser.canCreate;

  const [initialValues, setInitialValues] = useState(defaultInitialValues);
  // Holds the pending values while the role-linkage impact summary is shown
  // (Requirements 4.7–4.9). Non-null means the modal is open awaiting a
  // Confirm/Cancel decision.
  const [pendingImpactValues, setPendingImpactValues] = useState(null);

  // Edit hydration (Requirement 4.1): fetch the record only in edit mode.
  const {
    data,
    error: fetchError,
    isFetching: isFetchingRecord,
  } = useGetDataAccessAssignmentQuery({ id: idToEdit }, { skip: !idToEdit });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: dataAccessAssignmentFormValidation,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const { create, update, isLoading } = useForm({
    createMutation: useCreateDataAccessAssignmentMutation,
    updateMutation: useUpdateDataAccessAssignmentMutation,
    closeForm: onClose,
    setError: formik.setErrors,
  });

  // Hydrate the form once the record is retrieved (Requirement 4.1).
  useEffect(() => {
    if (idToEdit && data) {
      setInitialValues((prev) => mergeObjects(prev, data));
    }
  }, [data, idToEdit]);

  // Admin-role protection (Requirement 8): when the record is the Admin role's
  // all-client scope, its data scope is shown as "all_client" read-only and all
  // scope/role controls are disabled.
  const isAdminProtected = deriveAdminProtected(data);

  // Force the scope mode to "all_client" for the protected Admin assignment
  // (Requirement 8.2) without optimistically mutating anything else.
  useEffect(() => {
    if (isAdminProtected && formik.values.scope_mode !== "all_client") {
      formik.setFieldValue("scope_mode", "all_client");
    }
  }, [isAdminProtected]);

  // Scope-mode gating (Requirements 3.8, 3.9): the five scope selectors are
  // enabled only in "selected" mode. The Admin-protected assignment disables
  // every scope/role control regardless of mode (Requirement 8.3).
  // Controls are disabled when the assignment is Admin-protected (Requirement
  // 8.3) or when the user lacks manage permission (Requirements 1.4, 1.5).
  const controlsDisabled = isAdminProtected || !canManage;
  const scopeSelectorsEnabled =
    areScopeSelectorsEnabled(formik.values.scope_mode) && !controlsDisabled;
  const scopeSelectorsReadOnly = !scopeSelectorsEnabled;

  // Asset-scope selectors, each fed by its own dropdown search (Requirement 3.4).
  const { data: vehicleGroups, setSearchKey: setVehicleGroupSearch } = useDropDownSearch({
    useData: useGetVehicleGroupsQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.vehicle_group_ids,
  });

  const { data: vehicles, setSearchKey: setVehicleSearch } = useDropDownSearch({
    useData: useGetVehiclesQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.vehicle_ids,
  });

  const { data: deviceGroups, setSearchKey: setDeviceGroupSearch } = useDropDownSearch({
    useData: useGetDeviceGroupsQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.device_group_ids,
  });

  const { data: devices, setSearchKey: setDeviceSearch } = useDropDownSearch({
    useData: useGetDevicesQuery,
    simpleSearchKey: "name_or_device_id_cont",
    selectedIds: formik.values.device_ids,
  });

  const { data: events, setSearchKey: setEventSearch } = useDropDownSearch({
    useData: useGetEventTypesQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.event_ids,
  });

  // Roles selector (Requirement 3.7).
  const { data: roles, setSearchKey: setRoleSearch } = useDropDownSearch({
    useData: useGetRolesQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.role_ids,
  });

  // Sends the update/create request. Kept separate so it can be invoked either
  // directly (create, or edit without a role change) or after the impact
  // summary is confirmed (Requirement 4.8).
  const submitAssignment = (values) => {
    const formData = buildAssignmentPayload(values);
    if (idToEdit) {
      update({ id: idToEdit, formData });
    } else {
      create({ formData });
    }
  };

  const handleFormSubmit = (values) => {
    // Enforce the "selected mode requires at least one asset" rule
    // (Requirement 3.11). The schema expresses this as an object-level Yup test
    // whose error carries no field path, so Formik maps it to an empty error
    // set and would otherwise let submission through. Guard it here so the form
    // blocks submission and surfaces a validation message while retaining the
    // entered values.
    if (values.scope_mode === "selected" && !hasAnyScopeSelection(values)) {
      formik.setErrors({
        ...formik.errors,
        scope: getLocalizedString(
          "scope_required",
          "Select at least one asset when scope is 'selected'"
        ),
      });
      return;
    }

    // In edit mode, if the linked-role set changed from the originally loaded
    // set, intercept the submit and show the impact summary before sending
    // anything (Requirement 4.7). Create mode and unchanged role sets submit
    // directly.
    const roleSetChanged = idToEdit && data && !areRoleSetsEqual(values.role_ids, data.role_ids);
    if (roleSetChanged) {
      setPendingImpactValues(values);
      return;
    }
    submitAssignment(values);
  };

  // Confirm from the impact summary → send the update (Requirement 4.8).
  const handleImpactConfirm = () => {
    const values = pendingImpactValues;
    setPendingImpactValues(null);
    if (values) submitAssignment(values);
  };

  // Cancel from the impact summary → keep the form open, retain values, send
  // nothing (Requirement 4.9).
  const handleImpactCancel = () => {
    setPendingImpactValues(null);
  };

  // Edit fetch error (Requirement 4.2): surface the error and do not present an
  // editable form. The record could not be loaded, so no fields are rendered.
  if (idToEdit && fetchError) {
    return (
      <FormContainer
        resourceName={getLocalizedString("data_access_assignment", "Data Access Assignment")}
        closeForm={() => onClose(false)}
        handleFormSubmit={() => {}}
        idToEdit={idToEdit}
        isEditing
        error={{
          serverError: getLocalizedString(
            "data_access_record_load_error",
            "This data access assignment could not be loaded"
          ),
        }}
      >
        <p className="text-danger text-center">
          {getLocalizedString(
            "data_access_record_load_error",
            "This data access assignment could not be loaded"
          )}
        </p>
      </FormContainer>
    );
  }

  return (
    <FormContainer
      resourceName={getLocalizedString("data_access_assignment", "Data Access Assignment")}
      closeForm={() => onClose(false)}
      handleFormSubmit={formik.handleSubmit}
      idToEdit={idToEdit}
      isLoading={isLoading || isFetchingRecord}
      error={formik.errors}
      canSubmit={canManage}
    >
      <Components.QIInput
        label={getLocalizedString("name", "Name")}
        {...formik.getFieldProps("name")}
        error={formik.touched.name && formik.errors.name}
        disabled={controlsDisabled}
      />

      <Components.QIMultiSelectDropDown
        label={getLocalizedString("vehicle_groups", "Vehicle Groups")}
        selected={formik.values.vehicle_group_ids}
        data={vehicleGroups?.data || []}
        onChange={(value) => {
          formik.setFieldValue("vehicle_group_ids", value);
          setVehicleGroupSearch("");
        }}
        onSearch={(value) => setVehicleGroupSearch(value)}
        error={formik.touched.vehicle_group_ids && formik.errors.vehicle_group_ids}
        readOnly={scopeSelectorsReadOnly}
      />

      <Components.QIMultiSelectDropDown
        label={getLocalizedString("vehicles", "Vehicles")}
        selected={formik.values.vehicle_ids}
        data={vehicles?.data || []}
        onChange={(value) => {
          formik.setFieldValue("vehicle_ids", value);
          setVehicleSearch("");
        }}
        onSearch={(value) => setVehicleSearch(value)}
        error={formik.touched.vehicle_ids && formik.errors.vehicle_ids}
        readOnly={scopeSelectorsReadOnly}
      />

      <Components.QIMultiSelectDropDown
        label={getLocalizedString("device_groups", "Device Groups")}
        selected={formik.values.device_group_ids}
        data={deviceGroups?.data || []}
        onChange={(value) => {
          formik.setFieldValue("device_group_ids", value);
          setDeviceGroupSearch("");
        }}
        onSearch={(value) => setDeviceGroupSearch(value)}
        error={formik.touched.device_group_ids && formik.errors.device_group_ids}
        readOnly={scopeSelectorsReadOnly}
      />

      <Components.QIMultiSelectDropDown
        label={getLocalizedString("devices", "Devices")}
        labelKey="device_id"
        selected={formik.values.device_ids}
        data={devices?.data || []}
        onChange={(value) => {
          formik.setFieldValue("device_ids", value);
          setDeviceSearch("");
        }}
        onSearch={(value) => setDeviceSearch(value)}
        error={formik.touched.device_ids && formik.errors.device_ids}
        readOnly={scopeSelectorsReadOnly}
      />

      <Components.QIMultiSelectDropDown
        label={getLocalizedString("events", "Events")}
        selected={formik.values.event_ids}
        data={events?.data || []}
        onChange={(value) => {
          formik.setFieldValue("event_ids", value);
          setEventSearch("");
        }}
        onSearch={(value) => setEventSearch(value)}
        error={formik.touched.event_ids && formik.errors.event_ids}
        readOnly={scopeSelectorsReadOnly}
      />

      {/* Scope-selection validation message (Requirement 3.11): shown when the
          scope mode is "selected" but no asset was chosen in any selector. */}
      {formik.errors.scope && (
        <p className="text-danger data-access-scope_error">{formik.errors.scope}</p>
      )}

      {/* Role Assignment separator — matches PDF mockup */}
      <h4 className="form-section-separator form-section-title font-bold mb-4">
        {getLocalizedString("role_assignment", "Role Assignment")}
      </h4>

      <Components.QIMultiSelectDropDown
        label={getLocalizedString("roles", "Roles")}
        selected={formik.values.role_ids}
        data={roles?.data || []}
        onChange={(value) => {
          formik.setFieldValue("role_ids", value);
          setRoleSearch("");
        }}
        onSearch={(value) => setRoleSearch(value)}
        error={formik.touched.role_ids && formik.errors.role_ids}
        readOnly={controlsDisabled}
      />

      {/* Role-linkage impact summary before saving (Requirements 4.7–4.9).
          Counts come from the assignment record when present; otherwise the
          modal degrades to the linked-role count with a generic advisory
          (design Open Question 3). */}
      {pendingImpactValues && (
        <RoleImpactSummaryModal
          show={Boolean(pendingImpactValues)}
          affectedUsers={data?.affected_users_count}
          affectedUserGroups={data?.affected_user_groups_count}
          linkedRoles={pendingImpactValues.role_ids?.length}
          onConfirm={handleImpactConfirm}
          onCancel={handleImpactCancel}
        />
      )}

      {/* Read-only audit details in edit mode (Requirements 6.2, 6.3, 6.4). */}
      {idToEdit && data && (
        <div className="data-access-audit">
          <AuditField
            label={getLocalizedString("created_by", "Created By")}
            value={formatAuditUser(data.created_by)}
          />
          <AuditField
            label={getLocalizedString("created_on", "Created On")}
            value={formatAuditDate(data.created_at)}
          />
          <AuditField
            label={getLocalizedString("updated_by", "Last Updated By")}
            value={formatAuditUser(data.updated_by)}
          />
          <AuditField
            label={getLocalizedString("updated_on", "Last Updated On")}
            value={formatAuditDate(data.updated_at)}
          />
        </div>
      )}
    </FormContainer>
  );
};
